// Canonical candidate SQL fragments, shared so /api/crm (the CRM Workspace browse +
// profile) and the search engine (lib/search) resolve candidate STAGE and OWNER
// identically. AGENTS.md: "Prefer one shared helper/query path for candidate phases,
// process stages, owners." Keeping these in one place stops the two surfaces drifting.
//
// `s` / `ref` / `ownerCol` are slug/owner SQL EXPRESSIONS supplied by the caller --
// a column ref (browse/filter) or a '$n' placeholder (single-record profile). The
// entity string and status labels are code constants, never user input, so the
// interpolation here is injection-safe; user values are always bound by the caller.

import { CV_HISTORY_LABELS } from '../cv-send-evidence.mjs';
import { legacyDealPlacementEver } from './legacy-history-sql.mjs';
import { notClientSql, isClientSql, CLIENT_TYPES } from './client-type.mjs';

// Historical placements also prove an interview process, including archived imports.
// Interview-process job-assignment statuses that count as "in an interview process".
export const IV_LABELS = "('1st IV','2nd IV','3rd IV','Offer','WFB','Contract')";

// Candidate lifecycle stage -- combines app deals AND the het externe systeem mirror
// (placements + job-assignment stages), so candidates placed/interviewing in
// het externe systeem count even without an app deal.
export const stagePlaced = (s) => `(EXISTS (SELECT 1 FROM rcrm_placements rp WHERE rp.candidate_slug = ${s}) OR EXISTS (SELECT 1 FROM rcrm_job_assignments ja WHERE ja.candidate_slug = ${s} AND ja.status_label = 'Placed') OR EXISTS (SELECT 1 FROM deals d WHERE d.data->>'candidateSlug' = ${s} AND d.data->>'track'='placement' AND d.deleted_at IS NULL AND COALESCE((d.data->>'signed')::boolean,false)))`;
export const stageIv = (s) => `(${stagePlaced(s)} OR ${legacyDealPlacementEver(s)} OR EXISTS (SELECT 1 FROM rcrm_job_assignments ja WHERE ja.candidate_slug = ${s} AND ja.status_label IN ${IV_LABELS}) OR EXISTS (SELECT 1 FROM deals d WHERE d.data->>'candidateSlug' = ${s} AND d.data->>'track'='placement' AND d.deleted_at IS NULL))`;
// Finer "ever" tiers for the system-rating pipeline-warmth ladder (L). These sit
// between stagePlaced (top) and stageIv (the catch-all "ooit interviewproces"):
//   stageOfferEver -- ever reached an Aanbieding/Offer (app deal status OR the
//     imported Offer/WFB/Contract job-assignment labels).
//   stageIv3Ever   -- ever reached IV3 or deeper (app deal iv3Date set OR the
//     imported '3rd IV' label). Offer/WFB/Contract already rank higher via
//     stageOfferEver, so this tier is the "3rd IV but no offer yet" band.
// The ladder checks Placed -> Offer -> IV3 -> stageIv top-down, so the overlap of
// IV_LABELS (which still includes Offer/WFB/Contract) inside stageIv is harmless.
export const stageOfferEver = (s) => `(EXISTS (SELECT 1 FROM rcrm_job_assignments ja WHERE ja.candidate_slug = ${s} AND ja.status_label IN ('Offer','WFB','Contract')) OR EXISTS (SELECT 1 FROM deals d WHERE d.data->>'candidateSlug' = ${s} AND d.data->>'track'='placement' AND d.data->>'status'='Aanbieding arbeidscontract'))`;
export const stageIv3Ever = (s) => `(EXISTS (SELECT 1 FROM rcrm_job_assignments ja WHERE ja.candidate_slug = ${s} AND ja.status_label = '3rd IV') OR EXISTS (SELECT 1 FROM deals d WHERE d.data->>'candidateSlug' = ${s} AND d.data->>'track'='placement' AND NULLIF(d.data->>'iv3Date','') IS NOT NULL))`;
// "Sourced" = the candidate has been actively worked at the CV/proposal level:
// an app sourcing deal, a CV send (local cv_sends), or any het externe systeem job
// assignment. The Placed/IV tiers are checked first in stageCase, so an IV or
// Placed assignment is classified higher; everything else worked lands here.
export const stageSourced = (s) => `(EXISTS (SELECT 1 FROM deals d WHERE d.data->>'candidateSlug' = ${s} AND d.data->>'track'='sourcing') OR EXISTS (SELECT 1 FROM cv_sends cvs WHERE cvs.candidate_slug = ${s}) OR EXISTS (SELECT 1 FROM rcrm_job_assignments ja WHERE ja.candidate_slug = ${s}))`;
export const stageAny = (s) => `(EXISTS (SELECT 1 FROM deals d WHERE d.data->>'candidateSlug' = ${s}) OR EXISTS (SELECT 1 FROM rcrm_placements rp WHERE rp.candidate_slug = ${s}) OR EXISTS (SELECT 1 FROM rcrm_job_assignments ja WHERE ja.candidate_slug = ${s} AND ja.status_label IN ${IV_LABELS}))`;

// Job-assignment statuses at or past "CV Sent": de CV lag toen al bij de klant.
// Shared with the import, profile and workflow evidence readers.
export const CV_SENT_LABELS = `(${CV_HISTORY_LABELS.map((label) => `'${label}'`).join(',')})`;
// Successful sends and imported downstream milestones prove prior submission.
// A placement remains evidence even when archived or absent from assignments.
// Sourcing cards and generic Rejected/Didn't go for it snapshots do not prove a
// send. RecruitCRM assignments preserve the latest status, not a transition log.
export const cvSendEver = (s) => `(${legacyDealPlacementEver(s)} OR EXISTS (SELECT 1 FROM rcrm_placements rp WHERE rp.candidate_slug = ${s}) OR EXISTS (SELECT 1 FROM cv_sends cvs WHERE cvs.candidate_slug = ${s} AND cvs.status = 'sent') OR EXISTS (SELECT 1 FROM rcrm_job_assignments ja WHERE ja.candidate_slug = ${s} AND ja.status_label IN ${CV_SENT_LABELS}))`;

// --- CURRENT stage: IV/Sourced legs are active ON THE APP, the placed leg is
// wider (owner decision 6 aug 2026) ---
// The IV and Sourced legs read only app-native deals/cv_sends. Otherwise the
// thousands of imported, historical job-assignment stages (265 "1st IV", ...)
// read as if the candidate were live "in Interview Process" right now, drowning
// out the handful of genuinely open app processes. App-native processes are the
// only ones a user can manually archive, so they are the source of truth for
// "what is actually open"; imported IV/sourcing history stays one filter away
// via the Ooit flags.
// The PLACED leg does fold in the mirror (owner decision 6 aug 2026, display/
// query side, no data backfill): a placement is terminal, so an imported
// 'Placed' assignment or rcrm_placements row is the candidate's actual current
// state, not stale history. Without those arms ~65 candidates placed only in
// het externe systeem read Unsourced on their own profile while company pages
// show them as Placed. At ~274 terminal rows the drown-out rationale above does
// not apply to this leg. All three arms have candidate-slug indexes
// (idx_deals_candidate_slug, idx_rcrm_assign_candidate,
// idx_rcrm_placements_candidate).
// A SIGNED app placement is terminal too: archiving it = COMPLETING the
// placement, so a placed candidate must stay "Placement/Signed", not fall back
// to Unsourced. The deals arm (deleted_at IS NULL) counts a signed placement
// whether it is live OR archived (archived = data.archived flag, deleted_at
// still NULL) - post-merge zit elke gearchiveerde placement in deals, dus die
// arm is volledig. A director-DELETED deal (trash: deleted_at set) matches the
// arm niet, so it stops counting; the mirror arms can still keep the candidate
// placed when an imported placement remains.
export const stagePlacedActive = (s) => `(EXISTS (SELECT 1 FROM deals d WHERE d.data->>'candidateSlug' = ${s} AND d.deleted_at IS NULL AND d.data->>'track'='placement' AND COALESCE((d.data->>'signed')::boolean,false)=true) OR EXISTS (SELECT 1 FROM rcrm_job_assignments ja WHERE ja.candidate_slug = ${s} AND ja.status_label = 'Placed') OR EXISTS (SELECT 1 FROM rcrm_placements rp WHERE rp.candidate_slug = ${s} AND COALESCE(rp.archived,false)=false))`;
export const stageIvActive = (s) => `EXISTS (SELECT 1 FROM deals d WHERE d.data->>'candidateSlug' = ${s} AND d.deleted_at IS NULL AND d.data->>'track'='placement' AND COALESCE((d.data->>'archived')::boolean,false)=false AND COALESCE((d.data->>'signed')::boolean,false)=false AND COALESCE((d.data->>'rejected')::boolean,false)=false)`;
export const stageSourcedActive = (s) => `(EXISTS (SELECT 1 FROM deals d WHERE d.data->>'candidateSlug' = ${s} AND d.deleted_at IS NULL AND d.data->>'track'='sourcing' AND COALESCE((d.data->>'archived')::boolean,false)=false) OR EXISTS (SELECT 1 FROM cv_sends cvs WHERE cvs.candidate_slug = ${s}))`;
export const stageAnyActive = (s) => `(${stagePlacedActive(s)} OR ${stageIvActive(s)} OR ${stageSourcedActive(s)})`;

// De mijlpalen 'CV Send gedaan' en 'Geplaatst' van de onderscheidingsbalk (RecordBadges,
// lib/crm/candidate-milestones.mjs) lezen de expressies van het raster: 'CV Send gedaan'
// is het filter 'Ooit CV Send' (cvSendEver), 'Geplaatst' de plaatsingsarm van de fase
// (stagePlacedActive). Tot 26 september 2026 telde de balk zelf over de deals: elk
// sourcingproces gaf 'CV Send gedaan', ook zonder verzending, terwijl een verzonden CV
// zonder sourcingproces of een RecruitCRM-stap 'CV Sent' niets gaf, en een plaatsing die
// alleen in RecruitCRM staat gaf geen 'Geplaatst' (581 en 65 kandidaten op de
// testbranch). De eerste verzenddatum is de datum in de hover.
export const cvSendFirstAtSql = (s) => `(SELECT min(cvs.sent_at) FROM cv_sends cvs WHERE cvs.candidate_slug = ${s})`;
export const candidateMilestoneFactsSql = (s) => `${cvSendEver(s)} AS cv_send_ever, ${cvSendFirstAtSql(s)} AS cv_send_first_at, ${stagePlacedActive(s)} AS placed`;

// Laatste plaatsing van een kandidaat, als jsonb {date, company_slug}, over de twee
// bronnen heen: de geimporteerde rcrm_placements en de app-deals die getekend zijn.
//
// Waarom: een geplaatste kandidaat blijft PERMANENT herkenbaar als alumnus, met de
// markering "Plaatsing uit <maand jaar> bij <bedrijf>" (eigenaarsregel). Die tekst
// heeft precies twee dingen nodig, een datum en een bedrijf, dus geeft deze expressie
// die twee terug en niets meer.
//
// data->>'joining_date' staat vandaag op alle 266 mirror-rijen leeg; created_on is de
// werkende terugval en blijft dat tot de importbron die datum wel levert. De cast is
// bewust gepantserd met een datumpatroon: een enkele rommelwaarde in de JSON mag de
// hele query niet laten klappen. `s` is een slug-EXPRESSIE van de aanroeper (kolom of
// $n-placeholder), net als bij de buren hierboven, dus injectievrij.
export const alumniLatestExpr = (s) => `(SELECT jsonb_build_object('date', a.date, 'company_slug', a.company_slug)
   FROM (
     SELECT COALESCE(
              CASE WHEN rp.data->>'joining_date' ~ '^[0-9]{4}-[0-9]{2}-[0-9]{2}' THEN (rp.data->>'joining_date')::timestamptz END,
              rp.created_on) AS date,
            rp.company_slug AS company_slug
       FROM rcrm_placements rp
      WHERE rp.candidate_slug = ${s} AND COALESCE(rp.archived,false) = false
     UNION ALL
     SELECT CASE WHEN d.data->>'signedAt' ~ '^[0-9]{4}-[0-9]{2}-[0-9]{2}' THEN (d.data->>'signedAt')::timestamptz END AS date,
            NULLIF(d.data->>'companySlug','') AS company_slug
       FROM deals d
      WHERE d.data->>'candidateSlug' = ${s} AND d.deleted_at IS NULL
        AND d.data->>'track' = 'placement' AND COALESCE((d.data->>'signed')::boolean,false) = true
   ) a
  ORDER BY a.date DESC NULLS LAST
  LIMIT 1)`;

// Vertrokken bij plaatsing (10 sep 2026): de kandidaat IS geplaatst (de arm
// hierboven), maar werkt daar niet meer. Dat feit kent geen enkele bron, dus een
// gebruiker zet het zelf: candidate_overrides.data.placement_left_at (ISO-tijdstip
// van het markeren, placement_left_by = gebruiker). De markering geldt alleen als
// zij NA de laatste plaatsing is gezet: een nieuwe getekende plaatsing na het
// vertrek maakt de kandidaat vanzelf weer 'Placement/Signed', zonder opruimwerk.
// De overrides-toets staat voorop (PK-lookup op een sleutel die vrijwel nooit
// bestaat), zodat de plaatsingssubquery alleen voor gemarkeerde rijen draait.
export const stageLeftActive = (s) => `(EXISTS (SELECT 1 FROM candidate_overrides lo WHERE lo.candidate_slug = ${s} AND lo.data->>'placement_left_at' ~ '^[0-9]{4}-[0-9]{2}-[0-9]{2}' AND (lo.data->>'placement_left_at')::timestamptz > COALESCE(((${alumniLatestExpr(s)})->>'date')::timestamptz, '1970-01-01'::timestamptz)) AND ${stagePlacedActive(s)})`;
// Fase-prioriteit hoog -> laag. 'Left Placement' staat boven 'Placement/Signed':
// het is dezelfde plaatsing, een stap verder in de tijd.
export const stageCase = (s) => `CASE WHEN ${stageLeftActive(s)} THEN 'Left Placement' WHEN ${stagePlacedActive(s)} THEN 'Placement/Signed' WHEN ${stageIvActive(s)} THEN 'Interview Process' WHEN ${stageSourcedActive(s)} THEN 'Sourced' ELSE 'Unsourced' END`;

// Resolved company client_type: an app-side company_overrides.client_type wins (a
// signed BD proces promotes prospect->client, or a director's manual toggle), else
// the het externe systeem mirror column. Same override-wins, JOIN-FREE subquery shape as
// ownerIdExpr, so it drops into any query where a company slug expression is in
// scope WITHOUT adding (or colliding with) a company_overrides join -- note that in
// several reads `cov`/`o` already alias contact_overrides, so a join-based form
// could not be reused safely. ONE canonical expression so every surface (grid,
// zoeken, recent-views, deals, calls) resolves client_type identically. `slugRef` =
// the company slug SQL expr; `rawCol` = the raw client_type column (the fallback).
// notClientSql: de getekende typen ('client' + 'contracting') als SQL-poort, uit
// de gedeelde klanttypemodule.
export const clientTypeExpr = (slugRef, rawCol) => `COALESCE(NULLIF((SELECT cov.data->>'client_type' FROM company_overrides cov WHERE cov.company_slug = ${slugRef} LIMIT 1), ''), ${rawCol})`;

// Het klanttype van het bedrijf achter een proces, zoals het procesbord het toont:
// overlayClientType (lib/clientType.js) legt bij een gekoppeld bedrijf het HUIDIGE type
// (clientTypeExpr) over de stempel op de deal, en houdt de stempel alleen zonder
// gekoppeld bedrijf of bij een onbekende waarde. Het Processen-raster las tot 26
// september 2026 de stempel (deals.data.clientType). Elke volledige save zet die stempel
// opnieuw, nog voordat het tekenen het bedrijf naar klant zet, en zo toonden 137
// processen op de testbranch in het raster een ander type dan op het bord. `d` is de
// deal-alias van de buitenquery, een VERTROUWDE code-constante.
const DEAL_CLIENT_TYPES = CLIENT_TYPES.map((v) => `'${v}'`).join(', ');
export const dealClientTypeExpr = (d = 'p') => `COALESCE((SELECT dct.ct FROM (SELECT ${clientTypeExpr('dco.slug', 'dco.client_type')} AS ct FROM rcrm_companies dco WHERE dco.slug = ${d}.data->>'companySlug') dct WHERE dct.ct IN (${DEAL_CLIENT_TYPES})), ${d}.data->>'clientType')`;

// Of een bedrijf een getekend contract HEEFT is EEN expressie (wave 3, B8, 26 sep
// 2026). Vier bronnen, elk een bewijs van een handtekening: een overgenomen
// contract-intake (contract_intake, status 'accepted'), een goedgekeurde upload
// (client_contract_uploads, status 'approved'), een digitale ondertekening op /tekenen
// (contract_signings.signed_at gezet) en een contractbestand op het record
// (company_overrides.data.contract_url). Tot deze dag las elke lezer een eigen deel: de
// kaartcel 'Getekend contract' alleen het bestand, de statusregel bestand, intake of
// upload, de klantexport idem, het filter en de snelkeuze 'Contract getekend' alleen
// ondertekening of upload, en de leadgenerator alleen de ondertekening. Op de
// testbranch vond het filter 2 van de 423 bedrijven die de statusregel getekend noemde.
// Lezers: de kaartcel en de statusregel (has_signed_contract in resource=profile), het
// filter en de snelkeuze (COMPANY_CONTRACT_SIGNED in lib/search/catalogs-extra.mjs), de
// klantexport (lib/export/client-catalog.mjs), het leadsignaal 'getekend contract
// zonder lopend proces' (/api/director/generate-leads) en de afgeleide 'Getekende
// opdracht' van een klant (loadFeeTerms in lib/contract-terms.mjs, eigenaarsbesluit 26
// sep 2026). Er is geen JavaScript-kopie: wie het wil weten, selecteert deze
// expressie. WANNEER er getekend is ('Contract getekend op') is een ander feit:
// companySignedContractAtSql hieronder, over dezelfde vier bronnen.
// De vier bronnen staan EEN keer in CONTRACTBEWIJS: de tabel en de voorwaarde op de rij.
// De ja/nee-vraag en de datum lezen dezelfde lijst, zodat een bron nooit in de een wel
// en in de ander niet telt.
// Altijd true of false, nooit NULL. Join-vrij, met eigen aliassen (gtk_*), zodat hij in
// elke query past. `slugRef` is de SQL-expressie van de bedrijfsslug (een kolom of een
// $n), een vertrouwde code-constante.
const CONTRACTBEWIJS = Object.freeze({
  intake: { tabel: 'contract_intake', waar: (a) => `${a}.status = 'accepted'` },
  upload: { tabel: 'client_contract_uploads', waar: (a) => `${a}.status = 'approved'` },
  ondertekening: { tabel: 'contract_signings', waar: (a) => `${a}.signed_at IS NOT NULL` },
  bestand: { tabel: 'company_overrides', waar: (a) => `COALESCE(${a}.data->>'contract_url', '') <> ''` },
});
const bewijsRijen = (bron, a, slugRef) => `FROM ${bron.tabel} ${a} WHERE ${a}.company_slug = ${slugRef} AND ${bron.waar(a)}`;
// De rijen van een digitale ondertekening die als getekend tellen, voor wie de
// ondertekening zelf als gedateerde gebeurtenis leest (de klantstatusovergangen van
// lib/crm/cv-send-destination.mjs). Dezelfde voorwaarde als hierboven, geen kopie.
export const contractSigningRowsSql = (a, slugRef) => bewijsRijen(CONTRACTBEWIJS.ondertekening, a, slugRef);
export const companySignedContractSql = (slugRef) => `(EXISTS (SELECT 1 ${bewijsRijen(CONTRACTBEWIJS.intake, 'gtk_ci', slugRef)})
  OR EXISTS (SELECT 1 ${bewijsRijen(CONTRACTBEWIJS.upload, 'gtk_cu', slugRef)})
  OR EXISTS (SELECT 1 ${bewijsRijen(CONTRACTBEWIJS.ondertekening, 'gtk_cs', slugRef)})
  OR EXISTS (SELECT 1 ${bewijsRijen(CONTRACTBEWIJS.bestand, 'gtk_ov', slugRef)}))`;

// WANNEER een bedrijf tekende ('Contract getekend op') is EEN expressie over dezelfde
// vier bronnen: de datum van het bewijs (eigenaarsbesluiten 27 sep 2026). Een digitale
// ondertekening wint, met de laatste signed_at. Zonder ondertekening telt de laatste
// datum van de andere drie:
//   - een overgenomen intake: intakeDatumSql hieronder. Een geimporteerd contract telt
//     met zijn papieren datum, of zonder datum; elke andere intake met decided_at, en
//     created_at voor een rij zonder besluitmoment (created_at is NOT NULL);
//   - een goedgekeurde upload: decided_at, anders created_at (ook NOT NULL);
//   - het contractbestand op het record: bestandDatumSql hieronder.
// Tot deze dag las het filter 'Contract getekend op' alleen de digitale ondertekening
// (testbranch: 1 van de 420 getekende klanten had een datum) en rekende de klantexport
// zelf het maximum van ondertekening en intake uit. Elke arm leest zijn bron met de
// voorwaarde uit CONTRACTBEWIJS, en de ja/nee-vraag staat er als poort voor: een bedrijf
// zonder getekend contract heeft nooit een datum. Een getekend bedrijf kan wel zonder
// datum zijn: een geimporteerd contract zonder leesbare papieren datum ('leeg', niet het
// importmoment). De poort houdt het ook snel: CASE evalueert links naar rechts, Postgres
// hasht de EXISTS-toetsen over de hele tabel, en zo draaien de datumsubquery's alleen bij
// een getekend bedrijf (testbranch 27 sep 2026: het filter over alle bedrijven 27 ms; al
// zonder de papierregel was dat zonder poort 70 ms).
// Lezers: het filter 'Contract getekend op' (COMPANY_CONTRACT_SIGNED_AT in
// lib/search/catalogs-extra.mjs) en de kolom 'Getekend op' van de klantexport
// (lib/export/client-catalog.mjs). De bedrijfskaart toont geen datum. Join-vrij, met
// eigen aliassen (gtd_*, gpd*); `slugRef` als hierboven.
//
// De papieren tekendatum. De contractparser (lib/contract-parse.mjs) bewaart de datum
// op het papier als ruwe tekst in contract_intake.parsed.meta.signedDate. Op productie
// droegen 21 van de 432 overgenomen intakes er een (27 sep 2026), in drie vormen:
// DD-MM-JJJJ (18, '02-03-2020' is 2 maart 2020), JJJJ-MM-DD (2) en M/D/JJJJ (1,
// '9/13/2023'). papierDatumSql leest die tekst als datum, of als NULL:
//   - 'JJJJ-MM-DD' (PAPIER_ISO) als ISO-datum;
//   - 'D-M-JJJJ' met '-', '/' of '.' (PAPIER_DMJ) als dag-maand-jaar, behalve als het
//     middelste deel groter is dan 12 en het eerste hoogstens 12: dan maand-dag
//     ('9/13/2023' is 13 september 2023);
//   - alleen een jaar van vier cijfers. Al het andere is NULL, ook een onmogelijke datum
//     als '31-02-2024' en de sjabloontekst 'DD-MM-JJJJ'.
// Nooit een fout, ook niet op rommel: make_date krijgt alleen begrensde, dus geldige
// argumenten (de eerste van de maand plus dagen), en daarna moet de gebouwde datum
// hetzelfde jaar, dezelfde maand en dezelfde dag teruggeven. '31-02-2024' wordt zo 2
// maart en valt weg, zonder dat Postgres ooit een ongeldige datum hoeft te maken, ook
// niet als de planner een constante vooruit uitrekent. Geen ::date op vrije tekst (die
// hangt af van DateStyle en gooit bij onzin): alleen regexp_match, int-casts op cijfers,
// make_date en rekenen op een datum, allemaal IMMUTABLE. `tekst` is een vertrouwde
// SQL-expressie.
export const PAPIER_ISO = '^([0-9]{4})-([0-9]{2})-([0-9]{2})$';
export const PAPIER_DMJ = '^([0-9]{1,2})[-/.]([0-9]{1,2})[-/.]([0-9]{4})$';
export const papierDatumSql = (tekst) => `(SELECT CASE WHEN extract(year FROM gpd.x) = gpd.j AND extract(month FROM gpd.x) = gpd.m AND extract(day FROM gpd.x) = gpd.d THEN gpd.x END
      FROM (SELECT gpd0.j, gpd0.m, gpd0.d,
                   CASE WHEN gpd0.j IS NOT NULL AND gpd0.m IS NOT NULL AND gpd0.d IS NOT NULL
                        THEN make_date(greatest(gpd0.j, 1), least(greatest(gpd0.m, 1), 12), 1) + (least(greatest(gpd0.d, 1), 31) - 1) END AS x
              FROM (SELECT COALESCE(gpd1.iso[1], gpd1.dmj[3])::int AS j,
                           (CASE WHEN gpd1.iso IS NOT NULL THEN gpd1.iso[2] WHEN gpd1.dmj[2]::int > 12 AND gpd1.dmj[1]::int <= 12 THEN gpd1.dmj[1] ELSE gpd1.dmj[2] END)::int AS m,
                           (CASE WHEN gpd1.iso IS NOT NULL THEN gpd1.iso[3] WHEN gpd1.dmj[2]::int > 12 AND gpd1.dmj[1]::int <= 12 THEN gpd1.dmj[2] ELSE gpd1.dmj[1] END)::int AS d
                      FROM (SELECT regexp_match(btrim(${tekst}), '${PAPIER_ISO}') AS iso,
                                   regexp_match(btrim(${tekst}), '${PAPIER_DMJ}') AS dmj) gpd1) gpd0) gpd)`;
// Een papieren datum is een dag zonder tijd. Als moment staat hij op 12:00 UTC: dan is
// het in UTC en in Amsterdam dezelfde kalenderdag, voor het filter en voor de export.
const papierMomentSql = (tekst) => `((${papierDatumSql(tekst)} + time '12:00') AT TIME ZONE 'UTC')`;

// De datum van een contract_intake-rij (alias `a`). Een geimporteerd contract (source
// begint met 'import': de importrondes van 17 tot 21 juli 2026 en hun nazorg) draagt als
// enige eigen datum het importmoment, en dat is geen tekenmoment (eigenaarsbesluit 27
// sep 2026, 'papieren datum als die leesbaar is, anders leeg'): het telt met zijn
// papieren datum, of zonder datum, nooit met decided_at of created_at. Elke andere
// intake (een upload in de app, de klant-upgrade) telt met het moment van overnemen,
// decided_at, anders created_at.
const intakeDatumSql = (a) => `(CASE WHEN ${a}.source LIKE 'import%' THEN ${papierMomentSql(`${a}.parsed->'meta'->>'signedDate'`)} ELSE COALESCE(${a}.decided_at, ${a}.created_at) END)`;

// Het contractbestand draagt geen eigen datum: company_overrides kent geen stempel voor
// contract_url. Elke schrijver hangt het bestand aan op het moment dat hij de rij
// afhandelt waar de link naar wijst, dus die rij levert de datum.
// '/api/contracts?file=<id>' is een contract_intake-rij (overnemen in /api/contracts, de
// klant-upgrade, de contractimports), met de datum van intakeDatumSql, ook als die rij
// zelf later is afgewezen. '/api/contracts?download=<token>' is een digitale
// ondertekening, en die datum wint al in de eerste arm. Elke andere waarde telt zonder
// datum: company_overrides.updated_at schuift mee met elke latere bewerking van de rij
// en is geen tekenmoment. Het id is hoogstens 18 cijfers, zodat de cast naar bigint
// nooit de hele query laat klappen.
const bestandDatumSql = (a) => `(SELECT ${intakeDatumSql('gtd_f')} FROM contract_intake gtd_f
      WHERE gtd_f.id = (substring(${a}.data->>'contract_url' from '^/api/contracts\\?file=([0-9]{1,18})$'))::bigint)`;
export const companySignedContractAtSql = (slugRef) => `(CASE WHEN ${companySignedContractSql(slugRef)} THEN COALESCE(
  (SELECT max(gtd_cs.signed_at) ${bewijsRijen(CONTRACTBEWIJS.ondertekening, 'gtd_cs', slugRef)}),
  GREATEST(
    (SELECT max(${intakeDatumSql('gtd_ci')}) ${bewijsRijen(CONTRACTBEWIJS.intake, 'gtd_ci', slugRef)}),
    (SELECT max(COALESCE(gtd_cu.decided_at, gtd_cu.created_at)) ${bewijsRijen(CONTRACTBEWIJS.upload, 'gtd_cu', slugRef)}),
    (SELECT max(${bestandDatumSql('gtd_ov')}) ${bewijsRijen(CONTRACTBEWIJS.bestand, 'gtd_ov', slugRef)}))) END)`;

// Sync-safe display names. Renames live in the per-entity override tables and
// must win on every list/search surface, not only in the profile drawer.
export const companyNameExpr = (slugRef, rawCol) => `COALESCE(NULLIF((SELECT cov.data->>'company_name' FROM company_overrides cov WHERE cov.company_slug = ${slugRef} LIMIT 1), ''), ${rawCol})`;

// De plaats van een bedrijf (Kerngegevens 'Plaats') is EEN expressie. Volgorde: de
// app-override (company_overrides.data.city), dan de kolom city (het maatwerkveld
// 'Stad Company', CF6), dan data->>'city', dan data->>'locality'. Elke arm gaat door
// NULLIF, dus een lege override telt als afwezig.
// Lezers: de bedrijfskaart (resource=profile in app/api/crm/route.js), de
// Bedrijven-grid en het filter company.city (COMPANY_CITY_EFF in
// lib/search/catalogs-extra.mjs), de padbalk-hover (lib/crm/peek.mjs), de afstand
// kandidaat-bedrijf (resource=geo-distance), het Bezoekadres met de
// interviewbevestiging (lib/company-address.mjs, app/api/proces/iv-confirm/route.js),
// en als terugval de Plaats van een contactpersoon (CONTACT_CITY_EFF) en van een
// vacature (jobCityExpr hieronder).
// Er bestaat geen JavaScript-kopie van deze volgorde: wie de plaats nodig heeft,
// selecteert deze expressie. Op 24 sep 2026 las de hover alleen het native veld: bij
// 8.954 bedrijven met een plaats op de kaart toonde de hover er geen, bij 14 een
// LinkedIn-url.
// Een webadres is geen plaats (eigenaarsbesluit 24 sep 2026). 14 bedrijven droegen een
// LinkedIn-url in het native city-veld; elke url staat al op het bijbehorende contact.
// Daarom slaat elke spiegelarm (city, data->>'city', data->>'locality') een waarde met
// '://' of een begin 'www.' over bij het lezen, en blijft de mirror zelf ongemoeid. De
// override is bewuste invoer en telt zoals hij is. Van de 14 tonen er 11 nu hun Stad
// Company, 3 (KWS Infra, Mobilis TBI, Van Hattum en Blankevoort) geen plaats.
// webadresSql is die toets, EEN keer geschreven: ook de opruimlatch
// contact_overrides_city_url_cleanup_v1 in lib/db.js leest hem.
// `alias` is de alias van rcrm_companies in de query, of de tabelnaam als de query
// geen alias heeft.
export const webadresSql = (expr) => `(${expr}) ~* '(://|^www\\.)'`;
const spiegelPlaats = (arm) => `NULLIF(CASE WHEN ${webadresSql(arm)} THEN NULL ELSE ${arm} END,'')`;
export const companyCityExpr = (alias) => `COALESCE(NULLIF((SELECT cov.data->>'city' FROM company_overrides cov WHERE cov.company_slug = ${alias}.slug),''), ${spiegelPlaats(`${alias}.city`)}, ${spiegelPlaats(`${alias}.data->>'city'`)}, ${spiegelPlaats(`${alias}.data->>'locality'`)})`;

// ── Website, e-mail, telefoon en provincie van een bedrijf ──────────────────
// Elk EEN expressie, naast companyNameExpr en companyCityExpr (W2-7, 26 sep 2026).
//
// Website en E-mail volgen de regel van de kaartwaarde (overrideWinsSql, verderop in dit
// bestand): draagt company_overrides de sleutel, dan is dat de waarde, ook als hij leeg
// is (gewist); anders de spiegelkolom rcrm_companies.website of .email. Dat is precies
// wat update-company opslaat en wat de bedrijfskaart toont. Tot deze dag lazen de
// Bedrijven-grid (kolom, filter, sortering, trefwoord), de logo's in de contact-, vacature-
// en procesrijen en de anonimiteitstoets van het vacaturebord de spiegelkolom, en de kaart
// de override: op prod (25 sep 2026) stonden 109 websites en 8 adressen in de grid anders
// dan op de kaart, en misten 13 klanten met een lege RecruitCRM-website hun domein op de
// lijst van verboden termen. data->>'website' is geen tweede bron: waar hij gevuld is,
// is hij gelijk aan de kolom, en bij een in de app aangemaakt bedrijf is hij leeg.
//
// Telefoon (W3-5, 26 sep 2026) volgt dezelfde regel. De bedrijfskaart zet record.phone op
// de override als die de sleutel draagt, anders op de spiegelkolom rcrm_companies.phone
// (mergeCompanyOverrides in lib/crm/profile-record.mjs; update-company schrijft altijd
// tekst, dus een lege sleutel is gewist). data->>'phone' is geen bron: bij geen enkel
// bedrijf gevuld (testbranch, 26 sep 2026). Tot deze dag lieten de grid, het filter,
// phone_index, de telefoonzoeker, het Cmd-K-palet en de klantexport een gewist nummer
// terugkomen uit de spiegel, en lazen de dagbellijst, het Match-deck en de Land-regel
// (foreignCompanyExpr in lib/match/opportunities/focus.mjs) alleen de spiegel, terwijl 14 bedrijven op de
// testbranch een ander nummer op de kaart droegen dan in de spiegel. Het tweede nummer
// (alt_phone) bestaat alleen in de override en valt hier buiten.
//
// Provincie: de override (company_overrides.data.province) als die niet leeg is, anders de
// kolom province (het maatwerkveld 'Provincie Company', vaak een code of een meerkeuze als
// 'ZH; UT'). Een lege override telt als afwezig: de CityField schrijft een lege keuze, en
// dat was nooit een wisser. Dit is de expressie van de Bedrijven-grid en haar filter; de
// getoonde waarde is deze ruwe tekst door companyProvinceDisplay (lib/geo.mjs).
//
// Lezers: de kaart en het antwoord van update-company (companyCardCols in
// lib/crm/profile-record.mjs), de grid, het filter, de sortering en het trefwoord
// (COMPANY_WEBSITE_EFF, COMPANY_EMAIL_EFF, COMPANY_PHONE_EFF en COMPANY_PROVINCE_EFF in
// lib/search/catalogs-extra.mjs), de logo's (lib/company-website.mjs,
// lib/search/entities.mjs, resource=profile en de oude lijsten in app/api/crm), de
// verboden termen (lib/company-forbidden-terms.mjs), de klantexport
// (lib/export/client-catalog.mjs), de Land-regel (lib/match/opportunities/focus.mjs), de bellijst
// (app/api/call-lists), en voor de telefoon ook phone_index (lib/phone-map.js), het
// Cmd-K-palet (app/api/crm) en het Match-deck (lib/match/opportunities/sessions-sql.mjs). De telefoonzoeker
// (lib/search/phone-search.mjs) zoekt met dezelfde sleuteltoets (overrideKeySql).
// lib/one-fact-one-source.test.mjs (feiten bedrijfswebsite, bedrijfsmail,
// bedrijfstelefoon en bedrijfsprovincie) telt elke rauwe lezing van de spiegel.
// `alias`: de alias van rcrm_companies in de query, of de tabelnaam zonder alias. `ov`: de
// alias van een al gejoinde company_overrides-rij; zonder `ov` leest de expressie de
// override join-vrij, met een indexprobe per rij.
function bedrijfsKaartwaarde(key, spiegel) {
  return (alias, ov) => (ov
    ? overrideWinsSql(ov, key, spiegel(alias))
    : overrideWinsSubSql('company_overrides', 'company_slug', `${alias}.slug`, key, spiegel(alias)));
}
export const companyWebsiteExpr = bedrijfsKaartwaarde('website', (a) => `NULLIF(${a}.website,'')`);
export const companyEmailExpr = bedrijfsKaartwaarde('email', (a) => `NULLIF(${a}.email,'')`);
export const companyPhoneExpr = bedrijfsKaartwaarde('phone', (a) => `NULLIF(${a}.phone,'')`);
export const companyProvinceExpr = (alias, ov) => {
  const override = ov ? `${ov}.data->>'province'` : `(SELECT cov.data->>'province' FROM company_overrides cov WHERE cov.company_slug = ${alias}.slug)`;
  return `COALESCE(NULLIF(${override},''), NULLIF(${alias}.province,''))`;
};
// De regel van companyWebsiteExpr en companyEmailExpr als zoekarm: staat `pattern` in de
// kaartwaarde (ILIKE)? Voor een patroon met tekst ('%acme%') precies gelijk aan
// `${companyWebsiteExpr(alias)} ILIKE pattern`, maar de spiegelkolom staat kaal in de OR,
// zodat het trefwoord van de Bedrijven-grid zijn trigramindex
// (idx_rcrm_companies_website_trgm) en het vroege stoppen bij een pagina houdt: een
// override-rij wordt alleen gelezen bij een treffer op de spiegel (NOT EXISTS), en de
// treffers in de override komen als sluglijst uit een initplan, een keer per query.
// Gemeten op de testbranch (26 sep 2026): de eerste pagina van een trefwoord 8,7 ms
// (voorheen 7,5 ms op de kale spiegel), met de expressie zelf in de OR 57 tot 95 ms.
// `key`: 'website' of 'email'. `pattern`: een gebonden parameter ($n) of een andere
// VERTROUWDE expressie, nooit invoer.
export function companyCardValueIlikeSql(alias, key, pattern) {
  if (key !== 'website' && key !== 'email') throw new Error(`companyCardValueIlikeSql: onbekende sleutel ${key}`);
  return `((${alias}.${key} ILIKE ${pattern} AND NOT EXISTS (SELECT 1 FROM company_overrides kzo WHERE kzo.company_slug = ${alias}.slug AND kzo.data ? '${key}'))`
    + ` OR ${alias}.slug = ANY(ARRAY(SELECT kzo.company_slug FROM company_overrides kzo WHERE kzo.data->>'${key}' ILIKE ${pattern})))`;
}
// ── Werkgever uit vrije tekst -> het CRM-bedrijf ────────────────────────────
// Een kandidaat draagt zijn werkgever als losse tekst ('SmartLOXS') terwijl de
// klant in het CRM 'Smartloxs B.V.' heet. Een kale gelijkheid op de rauwe
// mirrornaam vindt die klant dus niet: het bedrijf staat er wel, maar wordt als
// vreemde organisatie getoond, zonder logo en zonder klantmarkering.
//
// De truc is dat de VARIANTEN aan de kant van de vrije tekst worden gemaakt, niet
// aan de kant van het bedrijf: de rechtsvorm gaat er van de getypte naam af en
// daarna komt elke geschreven vorm er weer achter ('smartloxs' -> 'smartloxs',
// 'smartloxs b.v.', 'smartloxs bv', ...). De bedrijfsnaam blijft daardoor een
// KALE gelijkheid en die is indexeerbaar: idx_rcrm_companies_name_lower staat op
// (LOWER(TRIM(company_name))) en bedient `= ANY(...)` gewoon mee. Een strip aan de
// bedrijfskant zou per aanroep een seq scan over rcrm_companies kosten (gemeten:
// 87 ms per werkgever) en deze expressie draait in de ⌘K-arm, bij elke toetsaanslag.
//
// Twee armen:
//   1. de rauwe mirrornaam tegen de variantenlijst (index);
//   2. het bedrijf staat in company_overrides met een hernoemde naam OF een
//      'ook bekend als'-alias die op een variant uitkomt.
// Arm 2 is `slug = ANY(ARRAY(SELECT ...))` en NIET `slug IN (SELECT ...)`: de
// ARRAY-vorm is een initplan die een keer draait, dus de planner kan er de
// primaire sleutel op zetten en beide armen als BitmapOr combineren. Met de
// IN-vorm werd het een gehashte subplan en viel de hele OR terug op een seq scan
// over rcrm_companies (gemeten: 15,7 ms per werkgever, 393 ms over 25 rijen,
// tegen 2,6 ms per werkgever nu).
// De override-arm dekt meteen de les Triodos 28 aug: een rauwe mirrorlezing liegt
// na een hernoem. Alles hoofdletter- en spatie-ongevoelig via lower(btrim(...)).
//
// `orgExpr` is een SQL-EXPRESSIE van de aanroeper (kolom, subquery of $n), net als
// bij de buren hierboven; `companyAlias` is een vertrouwde tabelalias.
//
// De vouw aan de tekstkant (wave 2, 26 sep 2026): naast de rechtsvorm gaan ook groep,
// nederland, benelux, international en nl eraf, als los woord en herhaald, plus een
// afsluitend leesteken (punt, komma, puntkomma): 'Thales Nederland B.V.' wordt 'thales',
// 'Fortezza Groep' 'fortezza', 'PitCrew NL' 'pitcrew', 'Data Science Lab.' 'data
// science lab', 'Realworks B.V,' 'realworks'. Met alleen de rechtsvorm matchten 1.174
// van de 25.535 werkgeverkoppelingen niet op hun eigen tekst (de backfills koppelden met
// de ruimere vouw van pickCompanyMatch) en gingen ze op zwart zodra die tekst werd
// bewerkt; met deze vouw zijn het er 423 (testbranch, 26 sep 2026). Alleen een los
// woord telt: 'Freelance.nl' blijft 'freelance.nl', dus de tekst 'Freelance' koppelt
// nooit aan dat bedrijf, en 'ManpowerGroup' verliest geen 'group'. Blijft er na het
// afknippen minder dan twee tekens over ('NL', 'B.V.'), dan geen afgeknipte varianten:
// alleen de tekst zelf telt. Dit is de enige vouw. EMPLOYER_SUFFIX_PATTERN en
// EMPLOYER_SUFFIX_FORMS zijn alleen geexporteerd zodat de test haar met hetzelfde
// patroon narekent (lib/crm/employer-link.test.mjs); een tweede kopie bestaat niet.
export const EMPLOYER_SUFFIX_PATTERN = '((\\s+|^)(b\\.?v\\.?|n\\.?v\\.?|holding|group|groep|nederland|benelux|international|nl)|[.,;])+\\s*$';
const LEGAL_SUFFIX_RE = `'${EMPLOYER_SUFFIX_PATTERN}'`;
// Elke schrijfwijze die het patroon hierboven afknipt, zodat de terugweg (variant
// bouwen) precies dekt wat de heenweg (rechtsvorm strippen) weghaalt.
export const EMPLOYER_SUFFIX_FORMS = Object.freeze(['', ' b.v.', ' b.v', ' bv.', ' bv', ' n.v.', ' n.v', ' nv.', ' nv', ' holding', ' group', ' groep', ' nederland', ' benelux', ' international', ' nl']);
const LEGAL_SUFFIX_FORMS = `ARRAY[${EMPLOYER_SUFFIX_FORMS.map((f) => `'${f}'`).join(', ')}]`;
const normText = (expr) => `lower(btrim(${expr}))`;
const stripLegalForm = (expr) => `btrim(regexp_replace(${normText(expr)}, ${LEGAL_SUFFIX_RE}, '', 'i'))`;
// De afgeknipte basis wordt een keer berekend (de afgeleide tabel evb) en alleen
// gebruikt als hij minstens twee tekens lang is.
export function employerCompanyVariantsSql(orgExpr) {
  return `(ARRAY[${normText(orgExpr)}] || ARRAY(SELECT evb.base || sfx FROM (SELECT ${stripLegalForm(orgExpr)} AS base) evb CROSS JOIN unnest(${LEGAL_SUFFIX_FORMS}) sfx WHERE char_length(evb.base) >= 2))`;
}

export function employerCompanyMatchSql(orgExpr, companyAlias = 'c') {
  const org = normText(orgExpr);
  const variants = employerCompanyVariantsSql(orgExpr);
  return `(${org} <> '' AND (
      ${normText(`${companyAlias}.company_name`)} = ANY(${variants})
      OR ${companyAlias}.slug = ANY(ARRAY(
        SELECT ecov.company_slug FROM company_overrides ecov
         WHERE ${normText("ecov.data->>'company_name'")} = ANY(${variants})
            OR EXISTS (SELECT 1 FROM jsonb_array_elements_text(COALESCE(ecov.data->'aliases','[]'::jsonb)) ealias
                        WHERE ${normText('ealias')} = ANY(${variants}))
      ))
    ))`;
}

// Bij meer treffers van employerCompanyMatchSql eerst het bedrijf waarvan de naam op de
// kaart precies de gezochte naam is, dan een alias of andere rechtsvorm.
export const employerExactFirstSql = (orgExpr, companyAlias = 'c') =>
  `(${normText(companyNameExpr(`${companyAlias}.slug`, `${companyAlias}.company_name`))} = ${normText(orgExpr)}) DESC`;

// ── De werkgeverkoppeling volgt de werkgeverstekst ──────────────────────────
// Eigenaarsregel 26 sep 2026 (wave 2, F2). candidate_overrides.current_company_slug
// is de CRM-koppeling onder de vrije tekst current_organization. Wie de tekst schrijft
// zonder zelf een bedrijf te kiezen, laat de koppeling niet staan: die wordt opnieuw
// opgelost of gewist. Tot deze regel bleef een oude koppeling hangen na een
// tekstwijziging (de LinkedIn-check van 15 sep zette Bol om in FedEx en liet de
// koppeling op Bol staan), en toonden de kaart, 'Werkt bij klant', de Werknemers van
// het bedrijf, de Match-telling en de CV-sendwaarschuwing het oude bedrijf onder de nieuwe
// naam.
//
// employerLinkSql is die ene regel als SQL-expressie; hij levert de slug of ''.
//   1. De huidige koppeling blijft staan zolang dat levende bedrijf nog op de tekst
//      matcht: 'Acme' wordt 'Acme B.V.' en springt niet naar een naamgenoot.
//   2. Anders het levende bedrijf dat op de tekst matcht: een getekende klant eerst
//      (dezelfde voorkeur als de werkgeverarm van het palet, candidateEmployerSql), dan
//      de kortste naam, dan de slug, zodat twee aanroepen hetzelfde antwoord geven.
//   3. Geen treffer of een lege tekst: '' (geen koppeling).
// Levend betekent geen tombstone in deletions; een weggemergd bedrijf staat daar ook.
// De matcher is employerCompanyMatchSql hierboven, er is geen tweede.
// Wie hem gebruikt: resolveEmployerLink (lib/crm/employer-link.mjs) voor elke schrijver
// in de app, de wijzigingstrigger candidate_change_note voor een syncwijziging van de
// spiegeltekst, en de eenmalige herstellatch employer_link_stale_repair_v1 (lib/db.js).
// `orgExpr` en `currentSlugExpr` zijn SQL-expressies van de aanroeper, net als bij
// employerCompanyMatchSql; 'NULL' betekent: er is geen huidige koppeling.
export function employerLinkSql(orgExpr, currentSlugExpr = 'NULL') {
  const levend = (a) => `NOT EXISTS (SELECT 1 FROM deletions eldx WHERE eldx.entity = 'company' AND eldx.slug = ${a}.slug)`;
  return `COALESCE(
      (SELECT elh.slug FROM rcrm_companies elh
        WHERE elh.slug = NULLIF(btrim(${currentSlugExpr}), '') AND ${levend('elh')}
          AND ${employerCompanyMatchSql(orgExpr, 'elh')}),
      (SELECT elm.slug FROM rcrm_companies elm
        WHERE ${employerCompanyMatchSql(orgExpr, 'elm')} AND ${levend('elm')}
        ORDER BY ${isClientSql(clientTypeExpr('elm.slug', 'elm.client_type'))} DESC, char_length(elm.company_name), elm.slug
        LIMIT 1),
      '')`;
}

// ── De processen van EEN kandidaat ───────────────────────────────────────────
// De koppeling tussen een proces en een kandidaat is de SLUG. De naam is puur de
// terugval voor processen van voor die koppeling bestond (15 sep 2026: 53 rijen met
// een naam en geen slug) en telt daarom ALLEEN als de rij zelf geen slug draagt.
// Zonder die voorwaarde leende elke naamgenoot de processen van de ander: twee
// kandidaten 'Silvain Smit' deelden een getekende plaatsing, badge en al, terwijl de
// een net van de website binnenkwam en de ander sinds februari bij een klant zit
// (66 van de 71 processen op een dubbele naam dragen gewoon hun eigen slug). De
// kandidaatnaam is dus nooit meer een tweede sleutel naast de slug.
// `alias` is de tabelalias in de query ('d'), of leeg als de query er geen heeft.
// Met `nameIsList` is nameParam een tekstarray (de naamset van lib/crm/deal-ref-match.mjs).
export const candidateDealMatchSql = (slugParam, nameParam, alias = '', { nameIsList = false } = {}) => {
  const d = alias ? `${alias}.` : '';
  const arm = nameIsList
    ? `${d}data->>'candidateName' = ANY(${nameParam})`
    : `NULLIF(${nameParam},'') IS NOT NULL AND ${d}data->>'candidateName' = ${nameParam}`;
  return `(${d}data->>'candidateSlug' = ${slugParam} OR (COALESCE(${d}data->>'candidateSlug','') = '' AND ${arm}))`;
};

// Dezelfde regel voor de processen van EEN bedrijf: de sleutel is companySlug, de
// klantNAAM is alleen de terugval voor deals van voor die koppeling. 83 bedrijfsnamen
// komen dubbel voor in de spiegel, dus zonder de slugloos-voorwaarde leest de ene
// vestiging de processen van de andere. `nameExpr` is een parameter ($2) of een SQL-
// expressie (companyNameExpr); met `nameIsList` is het een tekstarray voor ANY.
export const companyDealMatchSql = (slugExpr, nameExpr, alias = '', { nameIsList = false } = {}) => {
  const d = alias ? `${alias}.` : '';
  const arm = nameIsList
    ? `${d}data->>'clientName' = ANY(${nameExpr})`
    : `NULLIF(${nameExpr},'') IS NOT NULL AND ${d}data->>'clientName' = ${nameExpr}`;
  return `(${d}data->>'companySlug' = ${slugExpr} OR (COALESCE(${d}data->>'companySlug','') = '' AND ${arm}))`;
};

// ── De kaartwaarde: EEN betekenis van een override-sleutel ───────────────────
// Eigenaarsbesluit 25 september 2026 (wave 1, F1). Staat een sleutel in de
// override-JSON (candidate_overrides, contact_overrides), dan IS dat de waarde, ook
// als hij leeg is: leeg betekent gewist en wordt NULL. Ontbreekt de sleutel, of is er
// geen override-rij, dan geldt de RecruitCRM-spiegel. Dat is precies wat de kaart
// toont: mergeCandidateOverrides en mergeContactOverrides (lib/crm/profile-record.mjs)
// nemen elke aanwezige sleutel over, ook een lege.
// Tot deze regel schreef elke SQL-lezer COALESCE(NULLIF(<override>,''), <spiegel>):
// een gewist nummer stond leeg op de kaart en kwam terug in de grid, het filter, de
// telefoonzoeker, phone_index en de CV-ontvangers. Op 25 sep 2026 hadden 23
// kandidaten en 14 contactpersonen een gewist nummer met een spiegelnummer eronder.
//
// overrideKeySql: draagt de override-rij `ov` deze sleutel? Zonder rij (LEFT JOIN) is
// ov.data NULL, de toets dus NULL en de CASE valt naar de spiegel.
// overrideWinsSql: de kaartwaarde. `ov` is de alias van de override-rij, `key` de
// sleutel, `mirror` de SQL-expressie van de spiegel en `waarde` een optionele
// omzetting van de override-tekst (een getal, zie CAND_WORK_EX_YEAR). Alles
// VERTROUWD, nooit invoer. Schrijf deze CASE nergens anders uit, roep de helper aan:
// lib/one-fact-one-source.test.mjs (feit kaartwaarde) telt elke eigen kopie.
export const overrideKeySql = (ov, key) => `${ov}.data ? '${key}'`;
export const overrideWinsSql = (ov, key, mirror, waarde = (v) => v) => `CASE WHEN ${overrideKeySql(ov, key)} THEN ${waarde(`NULLIF(${ov}.data->>'${key}','')`)} ELSE ${mirror} END`;
// Dezelfde regel waar de buitenquery de override-rij niet joint: een scalaire
// subquery die altijd precies een rij oplevert (een lege rij met de override-rij er
// via de primaire sleutel aan gejoind), zodat 'geen override-rij' ook hier naar de
// spiegel valt. Een indexprobe per rij, net als de COALESCE-subquery die hij vervangt.
// De spiegelexpressie staat daardoor BINNEN de subquery: geef hem gekwalificeerd mee
// (c.first_name). Een kale kolomnaam die de override-tabel ook kent (data,
// updated_at, updated_by) zou naar de override-rij wijzen.
const overrideWinsSubSql = (table, slugCol, slugRef, key, mirror) =>
  `(SELECT ${overrideWinsSql('kov', key, mirror)} FROM (SELECT 1) kov_rij LEFT JOIN ${table} kov ON kov.${slugCol} = ${slugRef})`;
// De kaartwaarde van EEN veld van een kandidaat of contactpersoon, join-vrij. De naam
// is historisch: elk kaartveld met een spiegelkolom loopt erdoor (ook e-mail,
// telefoon, functie en werkgever).
export const candidateNamePartExpr = (slugRef, rawCol, part) => overrideWinsSubSql('candidate_overrides', 'candidate_slug', slugRef, part, rawCol);
// Contactpersoon-equivalent: het veld uit contact_overrides in plaats van
// candidate_overrides, verder dezelfde vorm.
export const contactNamePartExpr = (slugRef, rawCol, part) => overrideWinsSubSql('contact_overrides', 'contact_slug', slugRef, part, rawCol);

// ── De plaats en de provincie van een kandidaat ─────────────────────────────
// De Plaats en de Provincie van een kandidaat zijn elk EEN expressie (W2-3, 26 sep
// 2026). Eerst de override (candidate_overrides.data.city / .province) met de regel
// van overrideWinsSql: draagt de override de sleutel, dan is dat de waarde, ook leeg
// (gewist). Anders de spiegel, elke arm door NULLIF:
//   plaats     de kolom city (het maatwerkveld 'Stad', CF5; de sync schrijft het daar
//              en de EN-NL-backfill van 13 aug heeft alleen deze kolom vertaald), dan
//              data->>'city' (het eigen RecruitCRM-veld, vaak de Engelse LinkedIn-
//              naam), dan data->>'locality';
//   provincie  de kolom province (maatwerkveld 'Provincie', CF3, meestal een code
//              als ZH), dan data->>'province', dan data->>'state'.
// Gemeten op de testbranch (26 sep 2026): de kolom city is gevuld bij 61.286 van de
// 63.563 kandidaten, data->>'city' bij 60.766; waar ze allebei gevuld zijn en
// verschillen (3.232) is de kolom in 2.298 gevallen gelijk aan het maatwerkveld en
// data->>'city' in 126. Tot deze dag las de grid de kolom en de kaart data->>'city':
// grid Uden, kaart Rotterdam, en er ging niets stuk.
// De getoonde Provincie is de ruwe waarde hieronder door candidateProvinceDisplay
// (lib/geo.mjs): genormaliseerd zoals de grid dat al deed. Staat er geen provincie,
// dan is er geen: kolom, kaart en filter tonen dan allemaal '-'.
// Een plaatshouder is geen plaats. In het maatwerkveld Stad (en in data->>'city' en
// locality) staan waarden die geen woonplaats zijn: een regio (Randstad 2.437 keer in
// de kolom, 'X regio', 'Greater X Area', de Brabantse Stedenrij), een land of
// provincie (Netherlands, Nederland, Drenthe, Zeeland) of rommel (Pearsonspapi 782
// keer, '-', 'x', onbekend). Toen de kolom voorging kregen 286 kaarten zo'n waarde
// waar data->>'city' een echte plaats droeg (testbranch, 26 sep 2026). Net als de
// url-toets van companyCityExpr slaat daarom elke SPIEGELarm een plaatshouder over bij
// het lezen, zodat de volgende arm telt; de override is bewuste invoer en telt zoals
// hij is. De toets kijkt naar het deel voor de eerste komma ('The Randstad,
// Netherlands'), zonder hoofdletters; de spiegel zelf blijft ongemoeid.
// Lezers: de grid, het filter, de sortering en MatchRanking (CAND_CITY_EFF en
// CAND_PROVINCE_EFF in lib/search/catalog.mjs), de kaart en zijn kop (resource=profile
// en update-candidate in app/api/crm/route.js), de afstand in het procesvenster
// (resource=geo-distance), Kandidaten in de buurt (candidates-near-job), de bellijst,
// het belvenster (phone-lookup), het CV-profiel en entity_geo (lib/graph/geo.mjs).
// Wie de plaats nodig heeft, selecteert deze expressie; er is geen JavaScript-kopie
// van de volgorde. lib/one-fact-one-source.test.mjs (feit kandidaatplaats) telt elke
// rauwe lezing van de spiegel.
// `alias`: de alias van rcrm_candidates in de query (of de tabelnaam als de query er
// geen heeft). `ov`: de alias van een gejoinde candidate_overrides-rij; zonder `ov`
// leest de expressie de override join-vrij, met een indexprobe per rij.
// De plaatshouders: het deel voor de eerste komma, zonder hoofdletters en witruimte
// aan de randen. Gemeten over de drie spiegelarmen op de testbranch (26 sep 2026,
// 12.268 waarden), aangevuld met de spellingvarianten van dezelfde namen. Utrecht en
// Groningen zijn ook een stad en staan er dus niet in, 'Berkel & Rodenrijs' is een
// echte plaats, en een tikfout in een echte plaats (Tilbrug, Gronigen) is geen
// plaatshouder. Een vaste lijst en geen patroon: een regex kostte in het plaatsfilter
// over alle kandidaten 350 ms extra, deze lijst (een hashtabel in Postgres) 100 ms.
// Een nieuwe plaatshouder is een regel hier.
// De provincies die geen stad zijn, met hun schrijfwijzen uit de data, staan apart:
// dezelfde lijst zegt ook wanneer de Stad van een vacature voor een afstand meer dan
// een plaats is (isMeerDanEenPlaats hieronder, eigenaarsbesluit 26 sep 2026). EEN
// lijst, twee lezers; een nieuwe provincieschrijfwijze is een regel hier.
export const PROVINCIES_GEEN_STAD = Object.freeze([
  'brabant', 'noord-brabant', 'noord brabant', 'north brabant', 'noord-holland', 'noord holland', 'north holland',
  'zuid-holland', 'zuid holland', 'south holland', 'drenthe', 'flevoland', 'friesland', 'fryslan', 'fryslân',
  'gelderland', 'limburg', 'overijssel', 'zeeland',
]);
export const KANDIDAAT_PLAATSHOUDERS = Object.freeze([
  // rommel
  '-', '.', '?', 'x', 'onbekend', 'unknown', 'other', 'pearsonspapi',
  // land en werelddeel
  'netherlands', 'the netherlands', 'nederland', 'holland', 'europe', 'europa',
  // de Randstad
  'randstad', 'the randstad', 'de randstad',
  // provincies die geen stad zijn
  ...PROVINCIES_GEEN_STAD,
  // regio's rond een of twee steden
  'amsterdam area', 'amsterdam omgeving', 'amsterdam regio', 'antwerp metropolitan area', 'arnhem regio',
  'arnhem-nijmegen', 'arnhem-nijmegen omgeving', 'arnhem-nijmegen regio', 'arnhem-nijmegen region',
  'breda-tilburg', 'breda-tilburg area', 'breda-tilburg regio', 'den bosch regio', 'eindhoven area', 'eindhoven regio',
  'enschede regio', 'flevoland regio', 'ghent metropolitan area', 'greater enschede area', 'greater groningen area',
  'groningen regio', 'noord-holland regio', 'rotterdam & den haag regio', 'rotterdam and the hague', 'ruhr region',
  's-hertogenbosch area', 'uden-veghel', 'uden-veghel area', 'uden-veghel regio', 'uden-verghel omgeving',
  'utrecht area', 'utrecht regio',
  // de Brabantse Stedenrij, met zijn tikfouten
  'brabantine city row', 'brabantse stedenrij', 'brabantse stedenrij en omgeving', 'brabantse stenderij',
  'brabanste stedenrij', 'brabanste stederij', 'brabanste stenderij',
]);
const PLAATSHOUDERS_SQL = `ARRAY[${KANDIDAAT_PLAATSHOUDERS.map((v) => `'${v.replace(/'/g, "''")}'`).join(',')}]`;
const zonderPlaatshouder = (arm) => `CASE WHEN btrim(lower(split_part(${arm}, ',', 1))) = ANY(${PLAATSHOUDERS_SQL}) THEN NULL ELSE NULLIF(${arm},'') END`;
const kandidaatPlaatsSpiegel = (alias) => `COALESCE(${zonderPlaatshouder(`${alias}.city`)}, ${zonderPlaatshouder(`${alias}.data->>'city'`)}, ${zonderPlaatshouder(`${alias}.data->>'locality'`)})`;
const kandidaatProvincieSpiegel = (alias) => `COALESCE(NULLIF(${alias}.province,''), NULLIF(${alias}.data->>'province',''), NULLIF(${alias}.data->>'state',''))`;
const kandidaatLocatie = (key, mirror) => (alias, ov) => (ov
  ? overrideWinsSql(ov, key, mirror(alias))
  : overrideWinsSubSql('candidate_overrides', 'candidate_slug', `${alias}.slug`, key, mirror(alias)));
export const candidateCityExpr = kandidaatLocatie('city', kandidaatPlaatsSpiegel);
export const candidateProvinceExpr = kandidaatLocatie('province', kandidaatProvincieSpiegel);

// ── De talen van een kandidaat ───────────────────────────────────────────────
// Talen is EEN opslag: het CV-profiel, candidate_profile.data.talen (W2-11, 26 sep
// 2026). Daar schrijven de CV-editor (save-cv-profile), een nieuwe kandidaat met CV,
// de sourcing-extensie en de verzoening (lib/crm/reconcile.mjs), en daar lezen het CV,
// het anonieme CV en CvProfile. Draagt het profiel de sleutel, dan is dat de waarde,
// ook leeg (gewist in de editor); anders de RecruitCRM-spiegel
// (rcrm_candidates.data->>'language_skills'), dezelfde regel als overrideWinsSql.
// Tot die dag stond Talen op twee plekken: scripts/backfill-cv-fields.mjs schreef
// candidate_overrides.talen, het filter las die override, de gridkolom de kale spiegel
// en het CV het profiel (prod: 131 alleen in de override, 53 alleen in het profiel).
// De latch talen_profiel_v1 (lib/db.js) zette de overridewaarden in het profiel.
// Lezers: CAND_TALEN in lib/search/catalog.mjs (gridkolom, filter, sortering). Het CV
// leest het profieldocument zelf. `alias`: de alias van rcrm_candidates in de query.
// `prof`: de alias van een gejoinde candidate_profile-rij; zonder `prof` leest de
// expressie het profiel join-vrij, met een indexprobe per rij. De grid joint het profiel
// (lib/search/entities.mjs): een filter of sortering op Talen over alle kandidaten was met
// de indexprobe per rij 40 tot 80 procent trager dan met de join (testbranch, 26 sep 2026).
export const candidateTalenExpr = (alias, prof) => {
  const spiegel = `${alias}.data->>'language_skills'`;
  return prof
    ? overrideWinsSql(prof, 'talen', spiegel)
    : overrideWinsSubSql('candidate_profile', 'candidate_slug', `${alias}.slug`, 'talen', spiegel);
};
// Job-titel/-status idem, maar geparametriseerd op de slug-expressie in plaats van een
// vaste `j`-alias. lib/search/catalogs-extra.mjs exporteerde JOB_NAME_EFF/JOB_CATEGORY_EFF
// lang als kale string met `j.slug` hardgecodeerd; een query die rcrm_jobs niet als `j`
// aliast kon die constante dus niet hergebruiken en liet de merge dan maar weg (bug
// 21 aug 2026: bedrijfspagina en Structuur-boom toonden de rauwe mirrornaam). Deze vorm
// dropt in elke query waar een job-slug-expressie in scope is, join-vrij, net als
// companyNameExpr. JOB_NAME_EFF/JOB_CATEGORY_EFF zijn nu aanroepen hiervan.
export const jobNameExpr = (slugRef, rawCol) => `COALESCE(NULLIF((SELECT jo.data->>'name' FROM job_overrides jo WHERE jo.job_slug = ${slugRef}), ''), ${rawCol})`;
export const jobCategoryExpr = (slugRef, rawCol) => `COALESCE(NULLIF((SELECT jo.data->>'category' FROM job_overrides jo WHERE jo.job_slug = ${slugRef}), ''), ${rawCol})`;

// ── De plaats en de provincie van een vacature ──────────────────────────────
// Elk EEN expressie (W3-1, 26 sep 2026): het maatwerkveld van de vacature zelf ('Stad'
// of 'Provincie' in data.custom_fields, geschreven door de RecruitCRM-sync), anders de
// kaartwaarde van haar bedrijf (companyCityExpr, companyProvinceExpr hierboven). Een
// leeg veld telt als afwezig. Tot die dag las de Vacatures-grid (kolom, Plaats- en
// Provinciefilter, sortering) en de sollicitatierij alleen het bedrijf, rauw en zonder
// override of url-toets; lazen de suggesties, Match en de rematch eerst Stad; lazen
// Kandidaten in de buurt en de Match-longlist eerst Stad en dan alleen data->>'city' van
// het bedrijf; en ankerde de Afstand eerst op de positie van het bedrijf. Op de
// testbranch (26 sep 2026) had de grid bij 820 van de 1.743 vacatures een andere plaats
// dan deze expressie, wees Stad bij 725 (547 open) een andere plaats aan dan de
// bedrijfskaart, zodat grid en Match het oneens waren (EDSN: Stad Amersfoort, grid
// Utrecht), toonde de grid bij 19 een url, en vonden Kandidaten in de buurt en de
// longlist voor geen van de 67 in de app gemaakte vacatures een plaats (nu ontbreekt
// hij bij 3). Een vacature heeft geen override voor haar plaats: er is in de app geen
// schrijver.
// Lezers: JOB_CITY_EFF en JOB_PROVINCE_EFF in lib/search/catalogs-extra.mjs (grid,
// filter, sortering, sollicitatierij), JOB_CITY_SQL in lib/match/suggestions.mjs
// (suggesties, Match, rematch), Kandidaten in de buurt (app/api/crm/route.js), de
// Match-longlist, de zoekstart (app/api/search/seed/route.js), de bordspiegel
// (scripts/pa-mirror-enumerate.mjs), de LinkedIn-banners (app/api/banners/route.js) en
// het kantoorbord (app/api/dealtune/board/route.js). Tot 26 sep 2026 toonden banner en
// muur bewust de plaats van de klant; eigenaarsbesluit van die dag: de eigen plaats van
// de vacature, zonder eigen plaats die van de klant.
// Een AFSTAND rekent niet met deze plaats maar met jobGeoCityExpr hieronder (een
// regiowoord als 'Landelijk', een provincie of een reeks plaatsen is geen punt op de
// kaart): het Afstand-anker, de afstanden van dezelfde lezers en de geocodevulling
// (scripts/backfill-geocodes.mjs).
// lib/one-fact-one-source.test.mjs (feit vacatureplaats) telt elke eigen lezing van het
// maatwerkveld en elke rauwe plaats uit de spiegel.
// jsonb_typeof en niet COALESCE: een JSON-null of een scalar in custom_fields is geen
// SQL-NULL, dus COALESCE vangt hem niet en jsonb_array_elements klapt eruit ("cannot
// extract elements from a scalar"). De expressie loopt over de hele voorraad, dus een
// enkele rare rij zou de hele lijst een 500 geven. `alias`: de alias van rcrm_jobs in
// de query; de binnenste aliassen (jcf, jco) botsen niet met de buitenquery.
const jobCustomFieldSql = (alias, name) => `NULLIF((SELECT btrim(jcf->>'value')
        FROM jsonb_array_elements(CASE WHEN jsonb_typeof(${alias}.data->'custom_fields') = 'array'
                                       THEN ${alias}.data->'custom_fields' ELSE '[]'::jsonb END) jcf
       WHERE jcf->>'field_name' = '${name}' LIMIT 1), '')`;
// De terugval, EEN keer: de kaartplaats van het bedrijf van de vacature.
const jobBedrijfsplaatsSql = (alias) => `(SELECT ${companyCityExpr('jco')} FROM rcrm_companies jco WHERE jco.slug = ${alias}.company_slug)`;
export const jobCityExpr = (alias) => `COALESCE(${jobCustomFieldSql(alias, 'Stad')},
      ${jobBedrijfsplaatsSql(alias)})`;
export const jobProvinceExpr = (alias) => `COALESCE(${jobCustomFieldSql(alias, 'Provincie')},
      (SELECT ${companyProvinceExpr('jco')} FROM rcrm_companies jco WHERE jco.slug = ${alias}.company_slug))`;

// ── Een Stad die meer dan een plaats is ─────────────────────────────────────
// Eigenaarsbesluit 26 sep 2026 (vraag 'Regio'; wave 3b DI-2, wave 3c DI-3): staat er in
// de Stad van een vacature een gebied of een reeks plaatsen, dan blijft die overal staan
// zoals hij getypt is (jobCityExpr hierboven); alleen een AFSTAND rekent dan vanaf de
// plaats van de klant (jobGeoCityExpr hieronder). Meer dan een plaats is:
//   een regiowoord     'Landelijk', 'Randstad', 'Regio zuid' (VACATURE_REGIOWOORDEN);
//   een provincie      'Drenthe', 'Zuid-holland' (PROVINCIES_GEEN_STAD, de lijst van de
//                      plaatshouders van een kandidaat hierboven); Utrecht en Groningen
//                      zijn ook een stad en blijven een plaats;
//   meerdere plaatsen  'Den Bosch; Utrecht', 'Amersfoort/Den Bosch', 'Boxmeer, Arnhem':
//                      na splitsen op ; , / of | minstens twee niet-lege delen.
// Geen scheidingsteken: 'a/d' als los woord ('Capelle a/d IJssel'), 'a.d.' en ' en '
// ('Son en Breugel'). Een scheider aan het begin of eind met een enkel deel
// ('Den Bosch;') laat een plaats over. Een tikfout of afkapping ('Ei', 'UT') is geen
// gebied maar een foute plaats.
// Waarom: de geocoder gaf regiowoorden een punt, 'Regio zuid' in Brazilië, 'Overal' in
// Peru, 'Landelijk' bij Den Bosch, 'Randstad' bij Almere (testbranch, 26 sep 2026), en
// elke kandidaat stond dan op een verzonnen afstand van 24 Randstad-vacatures en 12
// landelijke. Op de testbranch (27 sep 2026) rekenden daarnaast 4 vacatures vanaf het
// middelpunt van hun provincie, en vond de geocoder de Stad van 26 met een reeks plaatsen
// niet (nominatim-miss): geen afstand, alleen het Afstand-filter viel bij 20 terug op de
// positie van het bedrijf. Nu rekenen 24 van die 30 vanaf de plaats van hun klant; bij 4
// is ook die plaats een reeks, 2 klanten hebben geen plaats.
// Twee toetsen, elk in JavaScript en in SQL uit dezelfde lijsten:
//   isRegionPlaceholder en regioWoordSql: alleen de regiowoorden. De geocoder
//     (lib/geocode.js) slaat een regiowoord over en bewaart er nooit een punt voor; de
//     latches geocode_regio_placeholders_v1 en entity_geo_regio_placeholders_v1
//     (lib/db.js) haalden de oude punten weg. Een provincie of een reeks plaatsen zoekt
//     de geocoder gewoon op: hij geocodeert ook de plaats van een kandidaat en een
//     bedrijf, en die vallen buiten dit besluit.
//   isMeerDanEenPlaats en meerDanEenPlaatsSql: regiowoord, provincie of meerdere
//     plaatsen, alleen voor de afstand van een vacature (jobGeoCityExpr).
// Een nieuw regiowoord of een nieuwe schrijfwijze van een provincie is een regel in de
// lijst; lib/one-fact-one-source.test.mjs (feit vacatureplaats, regel eigen-regiolijst)
// faalt op een tweede lijst.
// De vergelijking: kleine letters, streepjes en witruimte als een spatie, dus
// 'Noord- Oost nederland', 'noord-oost Nederland' en 'Noord oost nederland' zijn een
// woord. De afstandstoets haalt eerst ook de accenten weg: 'Fryslân' is 'fryslan'.
export const VACATURE_REGIOWOORDEN = Object.freeze([
  // het hele land
  'landelijk', 'nederland', 'overal',
  // een landsdeel zonder plaats
  'randstad', 'regio zuid', 'regio noord', 'zuidwest', 'noord-oost nederland', 'midden-nederland',
]);
export const regioWoordSleutel = (city) => String(city ?? '').toLowerCase().replace(/[\s-]+/g, ' ').trim();
const REGIOWOORDEN = new Set(VACATURE_REGIOWOORDEN.map(regioWoordSleutel));
/** Is deze plaatsnaam een regiowoord (geen punt op de kaart)? */
export const isRegionPlaceholder = (city) => REGIOWOORDEN.has(regioWoordSleutel(city));
const sqlTekstlijst = (woorden) => `ARRAY[${[...woorden].map((w) => `'${w.replace(/'/g, "''")}'`).join(',')}]`;
const REGIOWOORDEN_SQL = sqlTekstlijst(REGIOWOORDEN);
// De sleutel in SQL, dezelfde stappen als regioWoordSleutel.
const regioSleutelSql = (expr) => `btrim(regexp_replace(lower(${expr}), '[[:space:]-]+', ' ', 'g'))`;
// Dezelfde toets in SQL. `expr` is een vertrouwde SQL-expressie (een kolom of een
// subquery), nooit invoer. NULL blijft NULL.
export const regioWoordSql = (expr) => `(${regioSleutelSql(expr)} = ANY(${REGIOWOORDEN_SQL}))`;

// De afstandstoets. De sleutel: eerst de accenten weg (Unicode NFD, dan de losse
// accenttekens eruit; Postgres kent dezelfde normalize), dan de regiowoordsleutel.
export const plaatsSleutel = (stad) => regioWoordSleutel(String(stad ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, ''));
const plaatsSleutelSql = (expr) => regioSleutelSql(`regexp_replace(normalize(${expr}, NFD), E'[\\u0300-\\u036F]', '', 'g')`);
const MEER_DAN_EEN_PLAATS = new Set([...VACATURE_REGIOWOORDEN, ...PROVINCIES_GEEN_STAD].map(plaatsSleutel));
const MEER_DAN_EEN_PLAATS_SQL = sqlTekstlijst(MEER_DAN_EEN_PLAATS);
// Meerdere plaatsen: de scheidingstekens staan EEN keer, en beide patronen lezen
// JavaScript en Postgres (ARE) op dezelfde manier. Eerst wordt 'a/d' als los woord 'ad',
// dan zoekt het tweede patroon een teken van een deel, een scheider en daarna weer een
// teken van een deel. Dat is precies: na splitsen minstens twee niet-lege delen.
const PLAATS_SCHEIDERS = ';,/|';
const AAN_DE_PATROON = '(^|\\s)a/d(?=\\s|$)';
const MEERDERE_PLAATSEN_PATROON = `[^${PLAATS_SCHEIDERS}\\s][^${PLAATS_SCHEIDERS}]*[${PLAATS_SCHEIDERS}][${PLAATS_SCHEIDERS}\\s]*[^${PLAATS_SCHEIDERS}\\s]`;
const AAN_DE_RE = new RegExp(AAN_DE_PATROON, 'gi');
const MEERDERE_PLAATSEN_RE = new RegExp(MEERDERE_PLAATSEN_PATROON);
/** Is deze Stad van een vacature meer dan een plaats: een regiowoord, een provincie of meerdere plaatsen? */
export const isMeerDanEenPlaats = (stad) => MEER_DAN_EEN_PLAATS.has(plaatsSleutel(stad))
  || MEERDERE_PLAATSEN_RE.test(String(stad ?? '').replace(AAN_DE_RE, '$1ad'));
// Dezelfde toets in SQL, uit dezelfde lijsten en dezelfde patronen. `expr` is een
// vertrouwde SQL-expressie (een kolom), nooit invoer; hij staat er twee keer in, dus geef
// een kolom mee en geen subquery. NULL blijft NULL.
export const meerDanEenPlaatsSql = (expr) => `(${plaatsSleutelSql(expr)} = ANY(${MEER_DAN_EEN_PLAATS_SQL})
         OR regexp_replace(${expr}, '${AAN_DE_PATROON}', '\\1ad', 'gi') ~ '${MEERDERE_PLAATSEN_PATROON}')`;
// De plaats van een vacature voor een AFSTAND: de Stad als die EEN plaats is, anders
// de kaartplaats van de klant, dezelfde terugval als jobCityExpr. De Stad wordt EEN keer
// gelezen (de subquery jst). OFFSET 0 houdt Postgres ervan die subquery in te vouwen:
// zonder las hij het maatwerkveld twee keer, 48 in plaats van 25 ms over alle 1.752
// vacatures (testbranch, 26 sep 2026), en de afstandsjoin van de matchRanking zet deze
// expressie ook nog twee keer in zijn joinvoorwaarde.
// Lezers: jobCoords (app/api/search/route.js), Kandidaten in de buurt
// (app/api/crm/route.js), de Match-longlist, de zoekstart (app/api/search/seed/route.js),
// JOB_GEO_CITY_SQL in lib/match/suggestions.mjs (de suggesties, de rematch, Match) en
// de geocodevulling (scripts/backfill-geocodes.mjs). Wat een scherm TOONT blijft
// jobCityExpr. Heeft de klant geen plaats, dan is er geen plaats voor een afstand: de
// lezers geven dan hun bestaande uitkomst zonder plaats.
export const jobGeoCityExpr = (alias) => `COALESCE((SELECT jst.stad FROM (SELECT ${jobCustomFieldSql(alias, 'Stad')} AS stad OFFSET 0) jst
       WHERE NOT ${meerDanEenPlaatsSql('jst.stad')}),
      ${jobBedrijfsplaatsSql(alias)})`;

// CV Send is een universele contactvoorkeur, aan tenzij expliciet uitgezet.
// De legacy mailshot-sleutel blijft bestaan voor opgeslagen filters en API-clients.
// Prospectdoelgroepen hebben daarnaast hun eigen klanttypepoort.
export const cvSendPreferenceExpr = (ovDataCol) => `COALESCE((${ovDataCol}->>'mailshot')::boolean, true)`;
export const mailshotExpr = cvSendPreferenceExpr;

// Resolved owner: an app-side owner_override wins, else the het externe systeem owner_map.
export const ownerIdExpr = (ref, ent, ownerCol) => `COALESCE((SELECT oo.app_user_id FROM owner_overrides oo WHERE oo.entity = '${ent}' AND oo.slug = ${ref} LIMIT 1), (SELECT om.app_user_id FROM owner_map om WHERE om.rcrm_owner_id = ${ownerCol} LIMIT 1))`;
export const ownerNameExpr = (ref, ent, ownerCol) => `COALESCE((SELECT u.name FROM owner_overrides oo JOIN users u ON u.id = oo.app_user_id WHERE oo.entity = '${ent}' AND oo.slug = ${ref} LIMIT 1), (SELECT COALESCE(om.app_user_name, u.name) FROM owner_map om JOIN users u ON u.id = om.app_user_id WHERE om.rcrm_owner_id = ${ownerCol} LIMIT 1))`;

// SQL boolean fragment mirroring lib/gdpr.mjs offLimitSet(): candidate `<alias>` is
// Off-Limits from EITHER source (het externe systeem's off_limit_status_id OR the local
// director-set candidate_gdpr.off_limit flag). `alias` is a TRUSTED table alias for
// rcrm_candidates (never user input). The single source of truth for the union
// predicate, so the search catalog/views and the bulk-action guard can never drift
// from offLimitSet. The inner `g` alias is local to the subquery, so it is safe even
// when the outer query has its own. Lives HERE (not in lib/gdpr.mjs, that re-exports
// it) because this module is pure - the search catalog must stay `node --test`-baar.
//
// De mirror-arm is override-aware (eigenaarsbesluit 5 aug 2026): management kan een
// extern gezette Off-Limits in de app opheffen. Dat schrijft off_limit_status_id ''
// in candidate_overrides; de override wint blijvend van de mirror (app-wins, een
// sync raakt de overrides nooit aan). Sleutel afwezig in de override = mirror geldt.
//
// `overridesAlias` (package E3, lib/match/pairs.mjs): heeft de buitenquery
// candidate_overrides al EEN keer gejoind (LEFT JOIN op de primaire sleutel
// candidate_slug), geef die alias mee en de override-arm leest die rij in plaats van
// per kandidaat een subquery te openen. Zelfde waarde: de sleutel is uniek, dus de
// gejoinde rij IS wat de subquery met LIMIT 1 vindt, en geen rij geeft NULL in beide.
export function offLimitSqlExpr(alias = 'c', overridesAlias = null) {
  const overrideArm = overridesAlias
    ? `${overridesAlias}.data->>'off_limit_status_id'`
    : `(SELECT gov.data->>'off_limit_status_id' FROM candidate_overrides gov WHERE gov.candidate_slug = ${alias}.slug LIMIT 1)`;
  return `(NULLIF(COALESCE(
      ${overrideArm},
      ${alias}.data->>'off_limit_status_id'
    ),'') IS NOT NULL
    OR EXISTS (SELECT 1 FROM candidate_gdpr g WHERE g.candidate_slug = ${alias}.slug AND g.off_limit = true))`;
}

// Privacyverklaring-stand van een kandidaat (eigenaarsbesluit 9 sep 2026): vier
// bakjes, uit candidate_gdpr. Geen rij, status 'none' of nooit verzonden telt als
// 'Niet verstuurd'. `slugExpr` is een VERTROUWDE SQL-expressie (c.slug,
// p.data->>'candidateSlug', m.member_slug), nooit gebruikersinvoer. De inner-alias
// `pg` is lokaal aan de subquery. Een afnemer: het kandidaat- en procesfilter in de
// zoekcatalogus en het in-lijstfilter van de bellijsten; zo wijken die nooit af van
// elkaar of van het Beheer-register.
export { PRIVACY_STATUS_OPTIONS } from './privacy-status.mjs';
export function privacyStatusSqlExpr(slugExpr = 'c.slug') {
  return `COALESCE((SELECT CASE
      WHEN pg.status = 'accepted' THEN 'Verstuurd, geaccepteerd'
      WHEN pg.status = 'rejected' THEN 'Verstuurd, afgewezen'
      WHEN pg.status = 'sent' OR NULLIF(pg.sent_at, '') IS NOT NULL THEN 'Verstuurd, niet geaccepteerd'
      ELSE 'Niet verstuurd' END
    FROM candidate_gdpr pg WHERE pg.candidate_slug = ${slugExpr} LIMIT 1), 'Niet verstuurd')`;
}
// Tijdstip van acceptatie (TEXT ISO in candidate_gdpr), als timestamptz voor datumfilters.
export function privacyAcceptedAtSqlExpr(slugExpr = 'c.slug') {
  return `(SELECT NULLIF(pg.accepted_at, '')::timestamptz FROM candidate_gdpr pg WHERE pg.candidate_slug = ${slugExpr} AND pg.status = 'accepted' LIMIT 1)`;
}
